import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, CircularProgress, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Snackbar
} from '@mui/material';
import {
  Edit as EditIcon, Add as AddIcon, Delete as DeleteIcon,
  Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { formatVendorCurrency } from '../utils/currency';
import CustomInvoiceItemForm from '../components/CustomInvoiceItemForm';

interface CustomInvoiceItem {
  id: number;
  custom_title: string;
  case_title?: string;
  display_title?: string;
  billing_type: string;
  rate: number;
  quantity: number;
  amount: number;
  description?: string;
  is_custom: boolean;
  case_id?: number | null;
  case_estimate_id?: number | null;
}

interface CustomInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  period_start: string;
  period_end: string;
  opening_balance: number;
  total_billing: number;
  total_payments: number;
  net_amount: number;
  status: string;
  notes?: string;
  currency: string;
  billing_currency?: string | null;
  conversion_rate?: number | null;
  original_amount?: number | null;
  converted_amount?: number | null;
  original_total_billing?: number | null;
  vendor_firm?: { name: string };
  vendor_user?: { name: string; email: string; currency?: string | null; billing_currency?: string | null };
  items: CustomInvoiceItem[];
}

const CustomInvoiceView: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<CustomInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState<CustomInvoiceItem | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingRateItem, setEditingRateItem] = useState<number | null>(null);
  const [editedRates, setEditedRates] = useState<{[key: number]: number}>({});
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false);
  const [conversionRate, setConversionRate] = useState('');
  const [updatingConversion, setUpdatingConversion] = useState(false);

  useEffect(() => {
    if (invoiceId) {
      loadInvoice();
    }
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vendor-invoices/${invoiceId}`);
      if (response.data.success) {
        console.log('Invoice data received:', response.data.data);
        console.log('Vendor Firm:', response.data.data.vendor_firm);
        console.log('Vendor User:', response.data.data.vendor_user);
        setInvoice(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load invoice');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      setEditingItem(null);
    }
  };

  const handleAddItem = () => {
    setShowAddItemForm(true);
  };

  const handleItemAdded = () => {
    setShowAddItemForm(false);
    loadInvoice();
    setSuccess('Custom item added successfully!');
  };

  const handleEditItem = (item: CustomInvoiceItem) => {
    setEditingItem(item);
  };

  const handleItemUpdated = () => {
    setEditingItem(null);
    loadInvoice();
    setSuccess('Custom item updated successfully!');
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const response = await api.delete(`/vendor-invoices/${invoiceId}/custom-items/${itemId}`);
      if (response.data.success) {
        loadInvoice();
        setSuccess('Custom item deleted successfully!');
      } else {
        setError(response.data.message || 'Failed to delete item');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/approve`);
      if (response.data.success) {
        setSuccess('Invoice submitted for approval successfully!');
        loadInvoice();
      } else {
        setError(response.data.message || 'Failed to submit for approval');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit for approval');
    }
  };

  const handleCancelApproval = async () => {
    try {
      const response = await api.post(`/vendor-invoices/${invoiceId}/reject`);
      if (response.data.success) {
        setSuccess('Invoice approval request cancelled!');
        loadInvoice();
      } else {
        setError(response.data.message || 'Failed to cancel approval');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel approval');
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'draft': return 'default';
      case 'pending_approval': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'paid': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    if (!status) return 'Unknown';
    
    switch (status) {
      case 'draft': return 'Draft';
      case 'pending_approval': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'paid': return 'Paid';
      default: return status;
    }
  };

  const formatCurrency = (amount: number, currency?: string, useBillingCurrency: boolean = false) => {
    // Use billing currency if conversion is applied, otherwise use regular currency
    if (useBillingCurrency && invoice?.billing_currency) {
      return formatVendorCurrency(amount, invoice.billing_currency);
    }
    // Use vendor user currency if available, otherwise fall back to invoice currency or default
    const vendorCurrency = invoice?.vendor_user?.currency;
    const currencyCode = vendorCurrency || currency || 'INR';
    return formatVendorCurrency(amount, currencyCode);
  };

  const handleUpdateConversionRate = async () => {
    if (!invoice || !conversionRate) return;
    
    try {
      setUpdatingConversion(true);
      const response = await api.put(`/vendor-invoices/${invoice.id}/conversion-rate`, {
        conversion_rate: parseFloat(conversionRate)
      });
      if (response.data.success) {
        setSuccess('Conversion rate updated successfully');
        setConversionDialogOpen(false);
        setConversionRate('');
        loadInvoice();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update conversion rate');
    } finally {
      setUpdatingConversion(false);
    }
  };

  const openConversionDialog = () => {
    if (invoice) {
      setConversionRate(invoice.conversion_rate?.toString() || '');
      setConversionDialogOpen(true);
    }
  };

  // Check if this is a regular invoice (has case-based items from estimates)
  // Regular invoice = has items with case_id/case_estimate_id (from estimates)
  // Custom invoice = has items with is_custom: true or custom_title (manually added)
  const isRegularInvoice = () => {
    if (!invoice || !invoice.items) return false;
    // Return true if invoice has case-based items (from estimates), not custom items
    return invoice.items.some(item => (item.case_id || item.case_estimate_id) && !item.is_custom);
  };

  // Format billing type code to user-friendly label
  const getBillingTypeLabel = (type: string) => {
    switch (type) {
      case 'page_based': return 'Page Based';
      case 'case_based': return 'Case Based';
      case 'time_based': return 'Time Based';
      case 'flat_rate': return 'Flat Rate';
      default: return type ? type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';
    }
  };

  // Rate editing functions for regular invoices
  const handleEditRate = (itemIndex: number, currentRate: number) => {
    setEditingRateItem(itemIndex);
    setEditedRates({ ...editedRates, [itemIndex]: currentRate });
  };

  const handleSaveRate = async (itemIndex: number) => {
    if (!invoice) {
      setError('Invoice not loaded');
      return;
    }

    const newRate = editedRates[itemIndex];
    if (newRate === undefined || newRate < 0) {
      setError('Please enter a valid rate');
      return;
    }

    const item = invoice.items[itemIndex];
    if (!item) {
      setError('Item not found');
      return;
    }

    try {
      setError(null);
      
      // Call API to update the rate
      const response = await api.put(`/vendor-invoices/${invoice.id}/items/${item.id}/rate`, {
        rate: newRate
      });

      if (response.data.success) {
        // Reload the invoice to get updated data from server
        await loadInvoice();
        setEditingRateItem(null);
        setSuccess('Rate updated successfully');
      } else {
        setError(response.data.message || 'Failed to update rate');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update rate');
    }
  };

  const handleCancelRateEdit = (itemIndex: number) => {
    setEditingRateItem(null);
    const newEditedRates = { ...editedRates };
    delete newEditedRates[itemIndex];
    setEditedRates(newEditedRates);
  };

  const handleRateChange = (itemIndex: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    setEditedRates({ ...editedRates, [itemIndex]: numericValue });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/my-invoices')}
          sx={{ mt: 2 }}
        >
          Back to My Invoices
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {isRegularInvoice() ? 'Invoice' : 'Custom Invoice'} #{invoice.invoice_number}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {invoice.vendor_firm?.name || 'Unknown Firm'} • {invoice.vendor_user?.name || 'Unknown User'}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/my-invoices')}
          >
            Back to Invoices
          </Button>
          {invoice.status === 'draft' && !isRegularInvoice() && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
              onClick={handleEditToggle}
            >
              {isEditing ? 'Save Changes' : 'Edit Invoice'}
            </Button>
          )}
          {invoice.status === 'draft' && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmitForApproval}
            >
              Submit for Approval
            </Button>
          )}
          {invoice.status === 'pending_approval' && (
            <Button
              variant="outlined"
              color="warning"
              onClick={handleCancelApproval}
            >
              Cancel Approval
            </Button>
          )}
        </Box>
      </Box>

      {/* Status and Basic Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>Invoice Details</Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Invoice Number: <strong>{invoice.invoice_number}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date: <strong>{new Date(invoice.invoice_date).toLocaleDateString()}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Period: <strong>{new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}</strong>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip label={getStatusLabel(invoice.status)} color={getStatusColor(invoice.status)} size="small" />
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>Financial Summary</Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Opening Balance: <strong>{formatCurrency(Number(invoice.opening_balance) || 0)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Billing: <strong>{formatCurrency(Number(invoice.total_billing) || 0, undefined, true)}</strong>
                </Typography>
                {invoice.original_total_billing && invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Original: {formatVendorCurrency(invoice.original_total_billing, invoice.currency)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  Total Payments: <strong>{formatCurrency(Number(invoice.total_payments) || 0)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Net Amount: <strong>{formatCurrency(Number(invoice.net_amount) || 0, undefined, true)}</strong>
                </Typography>
                {invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
                  <>
                    {invoice.original_amount && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Original: {formatVendorCurrency(invoice.original_amount, invoice.currency)}
                      </Typography>
                    )}
                    {invoice.converted_amount && invoice.conversion_rate && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Converted: {formatVendorCurrency(invoice.converted_amount, invoice.billing_currency)}
                        {invoice.conversion_rate && (
                          <span> (Rate: {Number(invoice.conversion_rate).toFixed(4)})</span>
                        )}
                      </Typography>
                    )}
                    {!invoice.conversion_rate && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={openConversionDialog}
                        sx={{ mt: 0.5 }}
                      >
                        Add Conversion Rate
                      </Button>
                    )}
                  </>
                )}
                <Typography variant="body2" color="text.secondary">
                  Currency: <strong>{invoice.billing_currency || invoice.currency || 'USD'}</strong>
                </Typography>
              </Box>
            </Box>
          </Box>
          {invoice.notes && invoice.notes.trim() && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>Notes</Typography>
              <Typography variant="body2">{invoice.notes}</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Items Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {isRegularInvoice() ? 'Invoice Items' : 'Custom Invoice Items'}
            </Typography>
            {invoice.status === 'draft' && !isRegularInvoice() && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
              >
                Add Custom Item
              </Button>
            )}
          </Box>

          {(!invoice.items || invoice.items.length === 0) ? (
            <Alert severity="info">
              No custom items added yet. {invoice.status === 'draft' && 'Click "Add Custom Item" to get started.'}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item Title</TableCell>
                    <TableCell>Billing Type</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    {invoice.billing_currency && invoice.currency !== invoice.billing_currency && invoice.conversion_rate && (
                      <TableCell align="right">Converted Amount ({invoice.billing_currency})</TableCell>
                    )}
                    <TableCell>Description</TableCell>
                    {invoice.status === 'draft' && !isRegularInvoice() && <TableCell align="center">Actions</TableCell>}
                    {invoice.status === 'draft' && isRegularInvoice() && <TableCell align="center">Edit Rate</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.items?.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.display_title || item.case_title || item.custom_title || 'Untitled Item'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getBillingTypeLabel(item.billing_type || '')} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">
                        {editingRateItem === index ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                            <TextField
                              size="small"
                              type="number"
                              value={editedRates[index] || item.rate}
                              onChange={(e) => handleRateChange(index, e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                          </Box>
                        ) : (
                          formatCurrency(Number(item.rate) || 0)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {Number(item.quantity) || 0}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(Number(item.amount) || 0)}
                        </Typography>
                      </TableCell>
                      {invoice.billing_currency && invoice.currency !== invoice.billing_currency && invoice.conversion_rate && (
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            {formatVendorCurrency(
                              (Number(item.amount) || 0) * Number(invoice.conversion_rate),
                              invoice.billing_currency
                            )}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.description || '-'}
                        </Typography>
                      </TableCell>
                      {invoice.status === 'draft' && !isRegularInvoice() && (
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Edit Item">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditItem(item)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Item">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                      {invoice.status === 'draft' && isRegularInvoice() && (
                        <TableCell align="center">
                          {editingRateItem === index ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="Save">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleSaveRate(index)}
                                >
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleCancelRateEdit(index)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Tooltip title="Edit Rate">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditRate(index, item.rate)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Item Form Modal */}
      <Dialog 
        open={showAddItemForm} 
        onClose={() => setShowAddItemForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Custom Invoice Item</DialogTitle>
        <DialogContent>
          <CustomInvoiceItemForm
            invoiceId={invoice.id}
            invoiceStatus={invoice.status}
            onItemAdded={handleItemAdded}
            onCancel={() => setShowAddItemForm(false)}
            isInvoiceCreator={true}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Item Form Modal */}
      <Dialog 
        open={!!editingItem} 
        onClose={() => setEditingItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Custom Invoice Item</DialogTitle>
        <DialogContent>
          <CustomInvoiceItemForm
            invoiceId={invoice.id}
            invoiceStatus={invoice.status}
            editingItem={editingItem}
            onItemUpdated={handleItemUpdated}
            onCancel={() => setEditingItem(null)}
            isInvoiceCreator={true}
          />
        </DialogContent>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Conversion Rate Dialog */}
      {invoice && invoice.billing_currency && invoice.currency !== invoice.billing_currency && (
        <Dialog open={conversionDialogOpen} onClose={() => setConversionDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Conversion Rate</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Currency: {invoice.currency} → Billing Currency: {invoice.billing_currency}
              </Alert>
              <TextField
                fullWidth
                label={`Conversion Rate (${invoice.currency} to ${invoice.billing_currency})`}
                type="number"
                value={conversionRate}
                onChange={(e) => setConversionRate(e.target.value)}
                inputProps={{ min: 0.0001, step: 0.0001 }}
                helperText={`Enter the conversion rate from ${invoice.currency} to ${invoice.billing_currency}`}
                sx={{ mb: 2 }}
              />
              {invoice.original_amount && conversionRate && parseFloat(conversionRate) > 0 && (
                <Alert severity="success">
                  Converted Amount: {formatVendorCurrency(
                    invoice.original_amount * parseFloat(conversionRate),
                    invoice.billing_currency
                  )}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConversionDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateConversionRate}
              variant="contained"
              disabled={!conversionRate || parseFloat(conversionRate) <= 0 || updatingConversion}
            >
              {updatingConversion ? 'Updating...' : 'Update Conversion Rate'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default CustomInvoiceView;
